import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TenantService } from '../../core/services/tenant.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MenuCategory, MenuItem } from '../../core/models/menu.model';
import { TenantConfig } from '../../core/models/TenantConfig.model';

export interface PublicDeliveryRule {
  minimumDeliveryAmount: number;
  deliveryCharge: number;
}

export interface PublicDeliveryRulesResponse {
  status: string;
  message: string;
  deliveryRules: PublicDeliveryRule;
}

export interface OrderPauseDto {
  tenantId: number;
  isClosedDelivery: 0 | 1;
  isClosedCollection: 0 | 1;
  pauseStartAt?: string; // "HH:mm:ss" (or whatever backend returns)
  pauseUntil?: string | null; // "HH:mm:ss" | null
}

export interface ViewOrderPausePublicResponse {
  status: 'success' | 'failed';
  message?: string;
  data?: OrderPauseDto[];
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  constructor(
    private http: HttpClient,
    private tenantService: TenantService,
  ) {}

  getMenus(): Observable<any> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<any>(this.publicMenuUrl)
      : this.http.post<any>(this.publicMenuUrl, {
          action: 'viewPublicMenus',
        });

    return request$.pipe(map((resp) => resp));
  }

  getMenuItems(): Observable<MenuCategory[]> {
    return this.tenantService.tenantConfig$.pipe(
      filter((config) => config !== null),
      map((config) => config!.menu ?? []),
    );
  }

  updateCategoryStatus(categoryId: string, enabled: boolean): Observable<any> {
    return this.http.put(`/api/categories/${categoryId}/status`, { enabled });
  }

  updateMenuItems(updatedMenu: MenuCategory[]): Observable<any> {
    return this.tenantService.tenantConfig$.pipe(
      filter((config): config is TenantConfig => config !== null),
      switchMap((config) => {
        const url = `${environment.tenantConfigUrl}/${config._id}`;
        return this.http.put(url, { menu: updatedMenu });
      }),
    );
  }

  deleteMenuItem(itemId: string): Observable<any> {
    return this.http.delete(`/api/menu-items/${itemId}`);
  }

  getPublicDeliveryRules(): Observable<PublicDeliveryRulesResponse> {
    const isDummy = environment.useDummyTenantData;

    return isDummy
      ? this.http.get<PublicDeliveryRulesResponse>(this.publicDeliveryRulesUrl)
      : this.http.post<PublicDeliveryRulesResponse>(
          this.publicDeliveryRulesUrl,
          { action: 'viewTenantDeliveryRulesPublic' },
          { withCredentials: true },
        );
  }

  viewOrderPausePublic(): Observable<ViewOrderPausePublicResponse> {
    const isDummy = environment.useDummyTenantData;

    return isDummy
      ? this.http.get<ViewOrderPausePublicResponse>(this.publicOrderPauseUrl)
      : this.http.post<ViewOrderPausePublicResponse>(
          this.publicOrderPauseUrl,
          { action: 'viewOrderPausePublic' },
          { withCredentials: true },
        );
  }

  private get publicMenuUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/menu.json'
      : environment.menuConfigUrl;
  }

  private get publicDeliveryRulesUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/delivery-rules.json'
      : environment.tenantConfigUrl;
  }

  private get publicOrderPauseUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/order-pause.json'
      : environment.tenantConfigUrl;
  }
}
